import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Button } from './ui/Button';
import { TaskDetail, usersApi } from '../lib/api';
import { Check } from 'lucide-react';

interface EditTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskDetail;
  onSave: (data: {
    text: string;
    assigneeIds: string[];
    startDate: string | null;
    dueDate: string | null;
    priority: 'CRITICAL' | 'HIGH' | 'CURRENT';
  }) => void;
  isSaving: boolean;
}

export function EditTaskModal({ isOpen, onClose, task, onSave, isSaving }: EditTaskModalProps) {
  const [text, setText] = useState(task.text);
  const [priority, setPriority] = useState(task.priority);
  const [startDate, setStartDate] = useState(
    task.startDate ? format(new Date(task.startDate), 'yyyy-MM-dd') : ''
  );
  const [dueDate, setDueDate] = useState(
    task.dueDate ? format(new Date(task.dueDate), 'yyyy-MM-dd') : ''
  );
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(
    task.assignees.map(a => a.id)
  );

  const projectId = task.section.protocol?.project?.id;

  const { data: members = [] } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => usersApi.getProjectMembers(projectId!),
    enabled: !!projectId && isOpen,
  });

  // Обновляем состояние когда задача меняется
  useEffect(() => {
    setText(task.text);
    setPriority(task.priority);
    setStartDate(task.startDate ? format(new Date(task.startDate), 'yyyy-MM-dd') : '');
    setDueDate(task.dueDate ? format(new Date(task.dueDate), 'yyyy-MM-dd') : '');
    setSelectedAssignees(task.assignees.map(a => a.id));
  }, [task]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    onSave({
      text: text.trim(),
      assigneeIds: selectedAssignees,
      startDate: startDate || null,
      dueDate: dueDate || null,
      priority,
    });
  };

  const toggleAssignee = (userId: string) => {
    setSelectedAssignees(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Редактировать задачу"
      footer={
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1"
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!text.trim() || isSaving}
            className="flex-1"
          >
            Сохранить
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Текст задачи */}
        <div>
          <label className="block text-sm font-medium text-tg-text mb-2">
            Текст задачи
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-tg-secondary-bg text-tg-text placeholder:text-tg-hint border-2 border-transparent focus:border-tg-button focus:outline-none transition-colors duration-200 resize-none"
            placeholder="Введите текст задачи..."
            required
          />
        </div>

        {/* Приоритет */}
        <Select
          label="Приоритет"
          value={priority}
          onChange={(e) => setPriority(e.target.value as any)}
          options={[
            { value: 'CURRENT', label: '🟢 Текущая' },
            { value: 'HIGH', label: '🟠 Важная' },
            { value: 'CRITICAL', label: '🔴 Сверхважная' },
          ]}
        />

        {/* Даты */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="date"
            label="Дата начала"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            type="date"
            label="Дедлайн"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>

        {/* Ответственные */}
        <div>
          <label className="block text-sm font-medium text-tg-text mb-2">
            Ответственные
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto bg-tg-secondary-bg rounded-xl p-3">
            {members.length === 0 ? (
              <p className="text-sm text-tg-hint text-center py-2">
                Нет доступных участников
              </p>
            ) : (
              members.map((member) => {
                const isSelected = selectedAssignees.includes(member.id);
                
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => toggleAssignee(member.id)}
                    className={`
                      w-full flex items-center gap-3 p-2 rounded-lg transition-colors
                      ${isSelected 
                        ? 'bg-tg-button/10 border-2 border-tg-button' 
                        : 'bg-tg-bg hover:bg-tg-bg/50 border-2 border-transparent'
                      }
                    `}
                  >
                    <div className={`
                      w-5 h-5 rounded flex items-center justify-center flex-shrink-0
                      ${isSelected ? 'bg-tg-button' : 'bg-tg-hint/20'}
                    `}>
                      {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm text-tg-text">
                        {member.lastName || ''} {member.firstName}
                      </p>
                      {member.username && (
                        <p className="text-xs text-tg-hint">@{member.username}</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}


























